// Cookie Consent — banner, preferences modal, and persistent reopen tab.
// Ported from the Rush Studio design handoff (Cookie Consent.html) into a
// self-contained React component. Mounts once, globally (see App.js), so it
// appears on every route — marketing and app alike.
//
// Google Consent Mode v2 compatible: everything defaults to DENIED before any
// tag loads; Google Analytics (GA4) is only injected once the visitor grants
// analytics. Set REACT_APP_GA_ID to your GA4 Measurement ID to enable GA — a
// public client-side id, so REACT_APP_* is the correct place for it. With no id
// set, consent is still recorded and honored; GA simply never loads.

import React, { useCallback, useEffect, useState } from 'react';
import './cookieConsent.css';

const STORAGE_KEY = 'rush_cookie_consent_v1';
const GA_ID = (process.env.REACT_APP_GA_ID || '').trim();

function readConsent() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY));
  } catch (e) {
    return null;
  }
}

function writeConsent(consent) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, ts: Date.now() }));
  } catch (e) {
    /* private mode / storage disabled — choice just isn't persisted */
  }
}

// ---- Google Consent Mode plumbing ----
function gtag() {
  window.dataLayer.push(arguments);
}

// Default every storage type to DENIED before any tag loads. Runs once.
function ensureConsentDefault() {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) window.gtag = gtag;
  if (window.__ccConsentDefaultSet) return;
  window.__ccConsentDefaultSet = true;
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
}

function loadGA() {
  if (!GA_ID || window.__gaLoaded) return;
  window.__gaLoaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.googletagmanager.com/gtag/js?id=' + GA_ID;
  document.head.appendChild(s);
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { anonymize_ip: true });
}

// Apply a consent decision: update Consent Mode + fire GA if allowed.
function applyConsent(consent) {
  const granted = !!consent.analytics;
  window.gtag('consent', 'update', { analytics_storage: granted ? 'granted' : 'denied' });
  if (granted) loadGA();
}

const CookieIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M21.5 12.5a9.5 9.5 0 1 1-9.9-9.5 3.2 3.2 0 0 0 3.2 3.7 3 3 0 0 0 2.8 3.5 3 3 0 0 0 3.9 2.3z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <circle cx="9" cy="10" r="1" fill="currentColor" />
    <circle cx="14" cy="15" r="1.1" fill="currentColor" />
    <circle cx="9.5" cy="15.5" r="0.9" fill="currentColor" />
    <circle cx="15" cy="9.5" r="0.8" fill="currentColor" />
  </svg>
);

export default function CookieConsent() {
  const [decided, setDecided] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  // Init on mount: set the Consent Mode default, then honor any prior choice.
  useEffect(() => {
    ensureConsentDefault();
    const saved = readConsent();
    if (saved) {
      applyConsent(saved);
      setAnalytics(!!saved.analytics);
      setDecided(true);
    } else {
      setBannerOpen(true); // first visit — ask
    }
  }, []);

  const commit = useCallback((consent) => {
    writeConsent(consent);
    applyConsent(consent);
    setAnalytics(!!consent.analytics);
    setDecided(true);
    setBannerOpen(false);
    setPrefsOpen(false);
  }, []);

  const acceptAll = useCallback(() => commit({ analytics: true }), [commit]);
  const rejectAll = useCallback(() => commit({ analytics: false }), [commit]);
  const savePrefs = useCallback(() => commit({ analytics }), [commit, analytics]);

  const openPrefs = useCallback(() => {
    const saved = readConsent();
    setAnalytics(saved ? !!saved.analytics : false);
    setPrefsOpen(true);
  }, []);

  const closePrefs = useCallback(() => setPrefsOpen(false), []);

  // Expose an imperative API so other surfaces (e.g. the Privacy page, a footer
  // "Cookie settings" link) can reopen preferences or reset the choice.
  useEffect(() => {
    window.RushCookies = {
      openPrefs,
      reset: () => {
        try {
          window.localStorage.removeItem(STORAGE_KEY);
        } catch (e) {
          /* ignore */
        }
        window.__gaLoaded = false;
        setPrefsOpen(false);
        setDecided(false);
        setBannerOpen(true);
      },
    };
    return () => {
      if (window.RushCookies && window.RushCookies.openPrefs === openPrefs) {
        delete window.RushCookies;
      }
    };
  }, [openPrefs]);

  // Close the modal on Escape while it's open.
  useEffect(() => {
    if (!prefsOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setPrefsOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [prefsOpen]);

  return (
    <>
      {bannerOpen && (
        <div
          className="cc-banner"
          role="dialog"
          aria-modal="false"
          aria-labelledby="ccBannerTitle"
        >
          <div className="cc-head">
            <div className="cc-cookie" aria-hidden="true">
              <CookieIcon />
            </div>
            <div className="cc-copy">
              <h2 id="ccBannerTitle">We use cookies</h2>
              <p>
                We use strictly necessary cookies to run Rush Studio, plus optional analytics
                cookies (Google Analytics) to understand how the product is used. You choose what
                we&rsquo;re allowed to load. See our <a href="/privacy">Privacy Policy</a>.
              </p>
            </div>
          </div>
          <div className="cc-actions">
            <button type="button" className="cc-btn cc-btn-ghost cc-btn-sm cc-manage" onClick={openPrefs}>
              Manage preferences
            </button>
            <button type="button" className="cc-btn cc-btn-ghost" onClick={rejectAll}>
              Reject all
            </button>
            <button type="button" className="cc-btn cc-btn-accent" onClick={acceptAll}>
              Accept all
            </button>
          </div>
        </div>
      )}

      {prefsOpen && (
        <div
          className="cc-scrim"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePrefs();
          }}
        >
          <div className="cc-modal" role="dialog" aria-modal="true" aria-labelledby="ccModalTitle">
            <div className="cc-modal-head">
              <div>
                <h2 id="ccModalTitle">Cookie preferences</h2>
                <p>
                  Manage how Rush Studio uses cookies. You can change this anytime from the cookie
                  button.
                </p>
              </div>
              <button type="button" className="cc-x" aria-label="Close" onClick={closePrefs}>
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                  <path
                    d="M3.5 3.5l8 8M11.5 3.5l-8 8"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="cc-modal-body">
              <div className="cc-cat">
                <div className="cc-cat-text">
                  <h3>Strictly necessary</h3>
                  <p>
                    Required for the site to work — sign-in sessions, security, and load balancing.
                    These can&rsquo;t be switched off.
                  </p>
                  <span className="cc-tag">Always active</span>
                </div>
                <label className="cc-switch">
                  <input
                    type="checkbox"
                    checked
                    disabled
                    readOnly
                    aria-label="Strictly necessary cookies (always on)"
                  />
                  <span className="cc-track" />
                </label>
              </div>
              <div className="cc-cat">
                <div className="cc-cat-text">
                  <h3>Analytics</h3>
                  <p>
                    Google Analytics cookies (<code>_ga</code>, <code>_gid</code>) that tell us which
                    features are used, so we can improve them. Anonymous and never used to identify
                    you.
                  </p>
                </div>
                <label className="cc-switch">
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    aria-label="Analytics cookies"
                  />
                  <span className="cc-track" />
                </label>
              </div>
            </div>
            <div className="cc-modal-foot">
              <button type="button" className="cc-btn cc-btn-ghost" onClick={rejectAll}>
                Reject all
              </button>
              <button type="button" className="cc-btn cc-btn-ghost" onClick={acceptAll}>
                Accept all
              </button>
              <button type="button" className="cc-btn cc-btn-accent cc-save" onClick={savePrefs}>
                Save my choices
              </button>
            </div>
          </div>
        </div>
      )}

      {decided && !bannerOpen && (
        <button
          type="button"
          className="cc-reopen"
          aria-label="Cookie preferences"
          title="Cookie preferences"
          onClick={openPrefs}
        >
          <CookieIcon />
        </button>
      )}
    </>
  );
}
